"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState, useEffect } from "react";
import { Sparkles, Workflow, BellRing, CalendarDays } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Calendar } from "@/components/ui/calendar";
export default function AiDrivenSuperpowers() {
  const [sortingProgress, setSortingProgress] = useState(0);
  const [date, setDate] = useState<Date | undefined>(new Date());
  useEffect(() => {
    const timer = setTimeout(() => {
      setSortingProgress(92);
    }, 500);
    return () => clearTimeout(timer);
  }, []);
  return <section data-section-id="123023" id="built-for-the-modern-user" className="relative w-full bg-background py-24 md:py-32 overflow-hidden dark">
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[30rem] h-[30rem] bg-primary/10 rounded-full blur-[150px] pointer-events-none" />

      <div className="container mx-auto px-6 max-w-6xl relative z-10">
        <div className="mb-16 max-w-2xl">
          <h2 className="text-4xl md:text-6xl font-black tracking-tighter text-foreground mb-4 uppercase">
            Built for the Modern User
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
            HalfOrder brings Toronto's food scene to your doorstep. Split costs, discover local favorites, and connect with fellow students through shared meal experiences.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          
          {}
          <Card className="md:col-span-2 group relative overflow-hidden rounded-2xl border border-border/40 bg-card/30 backdrop-blur-md transition-all duration-500 hover:border-primary/60 hover:shadow-[0_0_40px_-10px_var(--color-primary)] hover:-translate-y-1 flex flex-col justify-between">
            <CardHeader className="pb-2">
              <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/20">
                <Sparkles className="h-6 w-6 text-primary" strokeWidth={2} />
              </div>
              <CardTitle className="text-2xl font-bold tracking-tight">Smart Grouping</CardTitle>
              <CardDescription className="text-base mt-2 max-w-md text-muted-foreground">
                Our algorithm clusters orders from the same kitchen, slashing delivery fees by up to 50% for everyone in the group.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="rounded-xl bg-background/50 p-5 border border-border/30">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-bold text-foreground uppercase tracking-wider">Matching Efficiency</span>
                  <span className="text-sm font-mono text-primary font-bold">{sortingProgress}%</span>
                </div>
                <Progress value={sortingProgress} className="h-2 bg-primary/10" />
                <p className="text-xs text-muted-foreground mt-3 font-mono">
                  Scanning 48 active orders in Downtown Toronto...
                </p>
              </div>
            </CardContent>
          </Card>

          {}
          <Card className="md:col-span-1 group relative overflow-hidden rounded-2xl border border-border/40 bg-card/30 backdrop-blur-md transition-all duration-500 hover:border-primary/60 hover:shadow-[0_0_40px_-10px_var(--color-primary)] hover:-translate-y-1">
            <CardHeader>
              <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/20">
                <Workflow className="h-6 w-6 text-primary" strokeWidth={2} />
              </div>
              <CardTitle className="text-2xl font-bold tracking-tight">Live Tracking</CardTitle>
              <CardDescription className="text-base mt-2 text-muted-foreground">
                Real-time GPS updates so you know exactly when to meet your meal buddies at the lobby.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-3 mt-4">
                <div className="h-2 w-full rounded-full bg-gradient-to-r from-primary/40 to-transparent" />
                <div className="h-2 w-4/5 rounded-full bg-gradient-to-r from-primary/40 to-transparent" />
                <div className="h-2 w-2/3 rounded-full bg-gradient-to-r from-primary/40 to-transparent" />
              </div>
            </CardContent>
          </Card>

          {}
          <Card className="md:col-span-1 group relative overflow-hidden rounded-2xl border border-border/40 bg-card/30 backdrop-blur-md transition-all duration-500 hover:border-primary/60 hover:shadow-[0_0_40px_-10px_var(--color-primary)] hover:-translate-y-1">
            <CardHeader>
              <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/20">
                <BellRing className="h-6 w-6 text-primary" strokeWidth={2} />
              </div>
              <CardTitle className="text-2xl font-bold tracking-tight">Secure Wallet</CardTitle>
              <CardDescription className="text-base mt-2 text-muted-foreground">
                Automated cost-splitting at checkout. Pay your share, and we handle the rest instantly.
              </CardDescription>
            </CardHeader>
            <CardContent>
               <div className="mt-4 flex items-center gap-4 rounded-lg border border-border/30 bg-background/40 p-3">
                  <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                  <div className="space-y-1.5 flex-1">
                    <div className="h-1.5 w-1/2 rounded-full bg-primary/40" />
                    <div className="h-1.5 w-3/4 rounded-full bg-primary/20" />
                  </div>
               </div>
            </CardContent>
          </Card>

          {}
          <Card className="md:col-span-2 group relative overflow-hidden rounded-2xl border border-border/40 bg-card/30 backdrop-blur-md transition-all duration-500 hover:border-primary/60 hover:shadow-[0_0_40px_-10px_var(--color-primary)] hover:-translate-y-1 flex flex-col md:flex-row items-center">
            <div className="flex-1 w-full">
              <CardHeader>
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/20">
                  <CalendarDays className="h-6 w-6 text-primary" strokeWidth={2} />
                </div>
                <CardTitle className="text-2xl font-bold tracking-tight">Scheduled Feasts</CardTitle>
                <CardDescription className="text-base mt-2 max-w-sm text-muted-foreground">
                  Sync your class schedule to find recurring lunch partners and lock in the best student deals daily.
                </CardDescription>
              </CardHeader>
            </div>
            <div className="p-6 w-full md:w-auto flex justify-center md:justify-end">
              <div className="rounded-xl border border-border/30 bg-background/60 p-2 shadow-lg backdrop-blur-sm">
                <SafeHydrate><Calendar mode="single" selected={date} onSelect={setDate} className="pointer-events-none" /></SafeHydrate>
              </div>
            </div>
          </Card>

        </div>
      </div>
    </section>;
}